import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Button, Typography, TextField, CircularProgress, Chip, Divider, Dialog,
  Table, TableHead, TableBody, TableRow, TableCell, IconButton, Paper, Tooltip, Grid, Alert,
  Tabs, Tab,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LogoutIcon from "@mui/icons-material/Logout";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import LockResetIcon from "@mui/icons-material/LockReset";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SendIcon from "@mui/icons-material/Send";
import ReplayIcon from "@mui/icons-material/Replay";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HistoryIcon from "@mui/icons-material/History";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
dayjs.extend(duration);

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const LARAVEL_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();

interface PortalShop {
  id: number; customerCompanyName?: string; customerFirstName?: string; customerLastName?: string;
  customerEmail?: string; customerMobileNum?: string; customerCompanyPhoneNumebr?: string;
  customerCompanyEmail?: string; customerStreetNumber?: string; customerStreetName?: string;
  customerCity?: string; customerProvince?: string; customerPostalCode?: string;
  customerDescription?: string; customerImg?: string; portal_email?: string;
}
interface OrderItem { product_id: number; name: string; qty: number; unit?: string; price: number; subtotal: number; }
interface ShopOrder { id: number; shop_id: number; items: OrderItem[]; total: number; status: string; notes?: string; portal_note?: string; created_at: string; }
interface DeliveryInfo { date: string; day_name: string; route_name?: string; delivery_time?: string; cutoff: string; cutoff_label: string; }

const STATUS_META: Record<string, { label: string; bg: string; color: string }> = {
  pending:    { label: "Submitted",     bg: "#FFF8E1", color: "#F57F17" },
  confirmed:  { label: "Confirmed",     bg: "#E8F5E9", color: "#2E7D32" },
  processing: { label: "Under Review",  bg: "#E3F2FD", color: "#1565C0" },
  delivered:  { label: "Delivered",     bg: "#E8F5E9", color: "#2E7D32" },
  cancelled:  { label: "Cancelled",    bg: "#FFEBEE", color: "#C62828" },
};
const isEditable = (status: string) => status === "pending";
const isAccepted = (status: string) => ["confirmed", "processing", "delivered"].includes(status);

const authFetch = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem("portal_token") || "";
  return fetch(url, { ...options, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) } });
};
const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
// No pricing visible to restaurants

const PortalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { shopId } = useParams<{ shopId: string }>();

  const [profileOpen, setProfileOpen] = useState(false);
  const [shop, setShop] = useState<PortalShop | null>(null);
  const [nextDelivery, setNextDelivery] = useState<string | null>(null);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>(null);
  const [orders, setOrders] = useState<ShopOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState("");

  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState<Partial<PortalShop>>({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [curPw, setCurPw] = useState(""); const [newPw, setNewPw] = useState(""); const [confirmPw, setConfirmPw] = useState("");
  const [pwError, setPwError] = useState(""); const [pwSuccess, setPwSuccess] = useState(""); const [pwLoading, setPwLoading] = useState(false);

  const [noteOrderId, setNoteOrderId] = useState<number | null>(null);
  const [noteText, setNoteText] = useState(""); const [savingNote, setSavingNote] = useState(false);
  const [requestOrderId, setRequestOrderId] = useState<number | null>(null);
  const [requestMsg, setRequestMsg] = useState(""); const [savingRequest, setSavingRequest] = useState(false);
  const [creditStatus, setCreditStatus] = useState<any>(null);
  const [viewOrder, setViewOrder] = useState<ShopOrder | null>(null);
  const [orderTab, setOrderTab] = useState<"all" | "pending" | "processing" | "confirmed" | "delivered">("all");

  const tabCounts = useMemo(() => ({
    all: orders.length,
    pending: orders.filter(o => o.status === "pending").length,
    processing: orders.filter(o => o.status === "processing").length,
    confirmed: orders.filter(o => o.status === "confirmed").length,
    delivered: orders.filter(o => o.status === "delivered").length,
  }), [orders]);

  const filteredOrders = useMemo(() => {
    if (orderTab === "all") return orders;
    return orders.filter(o => o.status === orderTab);
  }, [orders, orderTab]);

  const loadAll = useCallback(async () => {
    const token = localStorage.getItem("portal_token");
    if (!token) { setLoading(false); navigate("/restaurant-portal/login", { replace: true }); return; }
    try {
      const [profileRes, ordersRes] = await Promise.all([
        authFetch(`${API_URL}/portal/profile`), authFetch(`${API_URL}/portal/orders`),
      ]);
      if (profileRes.status === 401) { localStorage.removeItem("portal_token"); localStorage.removeItem("portal_shop_id"); setLoading(false); navigate("/restaurant-portal/login", { replace: true }); return; }
      const [profileData, ordersData] = await Promise.all([profileRes.json(), ordersRes.json()]);
      if (profileData.success) {
        setShop(profileData.data); setNextDelivery(profileData.next_delivery || null);
        setProfileForm(profileData.data); setDeliveryInfo(profileData.delivery_info || null);
        if (profileData.credit_status) setCreditStatus(profileData.credit_status);
      }
      if (ordersData.success) {
        setOrders(ordersData.data || []);
        if (ordersData.next_delivery) setNextDelivery(ordersData.next_delivery);
        if (ordersData.delivery_info) setDeliveryInfo(ordersData.delivery_info);
      }
    } catch (e) { setError("Failed to load data. Please try again."); } finally { setLoading(false); }
  }, [navigate]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Countdown timer
  useEffect(() => {
    if (!deliveryInfo?.cutoff) { setCountdown(""); return; }
    const update = () => {
      const now = dayjs();
      const cutoff = dayjs(deliveryInfo.cutoff);
      const diff = cutoff.diff(now);
      if (diff <= 0) { setCountdown("Cutoff passed"); return; }
      const d = dayjs.duration(diff);
      const h = Math.floor(d.asHours());
      const m = d.minutes();
      setCountdown(h > 0 ? `${h}h ${m}m left` : `${m}m left`);
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [deliveryInfo?.cutoff]);

  const handleLogout = () => { localStorage.removeItem("portal_token"); localStorage.removeItem("portal_shop_id"); navigate("/restaurant-portal/login", { replace: true }); };
  const saveProfile = async () => { setSavingProfile(true); try { const res = await authFetch(`${API_URL}/portal/profile`, { method: "PUT", body: JSON.stringify(profileForm) }); const data = await res.json(); if (data.success) { setShop(data.data); setEditingProfile(false); } } finally { setSavingProfile(false); } };
  const handleChangePw = async () => { if (newPw !== confirmPw) { setPwError("Passwords do not match"); return; } if (newPw.length < 6) { setPwError("Minimum 6 characters"); return; } setPwLoading(true); setPwError(""); try { const res = await authFetch(`${API_URL}/portal/change-password`, { method: "PUT", body: JSON.stringify({ current_password: curPw, password: newPw, password_confirmation: confirmPw }) }); const data = await res.json(); if (!data.success) { setPwError(data.message || "Failed"); return; } setPwSuccess("Password changed"); setChangePwOpen(false); setCurPw(""); setNewPw(""); setConfirmPw(""); } catch (err: any) { setPwError(err.message); } finally { setPwLoading(false); } };
  const handleAddNote = async () => { if (!noteOrderId) return; setSavingNote(true); try { const res = await authFetch(`${API_URL}/portal/orders/${noteOrderId}/note`, { method: "PUT", body: JSON.stringify({ note: noteText }) }); const data = await res.json(); if (data.success) { setNoteOrderId(null); setNoteText(""); await loadAll(); } } finally { setSavingNote(false); } };
  const handleSendRequest = async () => { if (!requestOrderId || !requestMsg.trim()) return; setSavingRequest(true); try { const res = await authFetch(`${API_URL}/portal/orders/${requestOrderId}/request`, { method: "POST", body: JSON.stringify({ message: requestMsg }) }); const data = await res.json(); if (!data.success) { alert(data.message); return; } setRequestOrderId(null); setRequestMsg(""); await loadAll(); } catch (e: any) { alert(e.message); } finally { setSavingRequest(false); } };

  const isDeliveryDay = nextDelivery ? dayjs(nextDelivery).isSame(dayjs(), "day") : false;
  const isCutoffPassed = deliveryInfo?.cutoff ? dayjs().isAfter(dayjs(deliveryInfo.cutoff)) : false;

  const shopName = shop?.customerCompanyName || [shop?.customerFirstName, shop?.customerLastName].filter(Boolean).join(" ") || "My Portal";
  const address = [shop?.customerStreetNumber, shop?.customerStreetName, shop?.customerCity, shop?.customerProvince, shop?.customerPostalCode].filter(Boolean).join(", ");
  const pendingOrders = orders.filter(o => o.status === "pending");
  const lastOrder = orders.find(o => o.status !== "cancelled");
  const totalOrders = orders.filter(o => o.status !== "cancelled").length;
  const confirmedOrders = orders.filter(o => o.status === "confirmed" || o.status === "delivered");

  // AI-powered suggestions from backend
  interface Suggestion {
    product_id: number; name: string; qty: number; unit: string;
    avg_qty: number; last_qty: number; frequency: number; frequency_pct: number;
    trend: "increasing" | "decreasing" | "stable"; trend_pct: number;
    confidence: number; reason: string;
  }
  const [aiSuggestions, setAiSuggestions] = useState<Suggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsGenerated, setSuggestionsGenerated] = useState("");
  const [ordersAnalyzed, setOrdersAnalyzed] = useState(0);

  useEffect(() => {
    const portalToken = localStorage.getItem("portal_token");
    if (!portalToken || orders.length === 0) return;
    setSuggestionsLoading(true);
    authFetch(`${API_URL}/portal/suggestions`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          setAiSuggestions(data.data);
          setSuggestionsGenerated(data.generated || "");
          setOrdersAnalyzed(data.orders_analyzed || 0);
        }
      })
      .catch(() => {})
      .finally(() => setSuggestionsLoading(false));
  }, [orders.length]);

  // Keep old format for navigation compatibility
  const suggestedProducts = aiSuggestions.map(s => ({
    id: s.product_id, name: s.name, avgQty: s.qty, unit: s.unit, count: s.frequency,
  }));

  const goToNewOrder = () => navigate(`/restaurant-portal/${shopId}/order`);
  const goToEditOrder = (order: ShopOrder) => navigate(`/restaurant-portal/${shopId}/order`, { state: { editOrder: order } });
  const goToReorder = (order: ShopOrder) => navigate(`/restaurant-portal/${shopId}/order`, { state: { reorder: order } });
  const goToSuggestedOrder = (items?: typeof suggestedProducts) => {
    const selected = items || suggestedProducts;
    const cart: Record<number, { qty: string; unit: string }> = {};
    selected.forEach(sp => { cart[sp.id] = { qty: String(sp.avgQty), unit: sp.unit }; });
    navigate(`/restaurant-portal/${shopId}/order`, { state: { suggestedCart: cart } });
  };
  const goToSingleSuggested = (sp: typeof suggestedProducts[0]) => {
    navigate(`/restaurant-portal/${shopId}/order`, { state: { suggestedCart: { [sp.id]: { qty: String(sp.avgQty), unit: sp.unit } } } });
  };

  if (loading) return (
    <Box sx={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f6f8", zIndex: 1200 }}>
      <Box sx={{ textAlign: "center" }}><CircularProgress size={36} sx={{ color: "#009688", mb: 2 }} /><Typography fontSize={14} color="text.secondary">Loading your dashboard...</Typography></Box>
    </Box>
  );
  if (error) return (
    <Box sx={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2, bgcolor: "#f5f6f8", zIndex: 1200 }}>
      <Typography color="error" fontSize={15}>{error}</Typography>
      <Button onClick={loadAll} variant="contained" sx={{ borderRadius: 2, bgcolor: "#009688", textTransform: "none" }}>Retry</Button>
    </Box>
  );

  return (
    <Box sx={{ position: "fixed", inset: 0, bgcolor: "#f5f6f8", zIndex: 1200, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* ── HEADER ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e8e8e8", px: { xs: 2, md: 3 }, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box onClick={() => setProfileOpen(true)}
            sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "#009688", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", cursor: "pointer", transition: "transform 0.15s", "&:hover": { transform: "scale(1.05)" } }}>
            {shop?.customerImg
              ? <img src={`${LARAVEL_URL}/${shop.customerImg}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: 16 }}>{shopName.charAt(0)}</Typography>}
          </Box>
          <Box sx={{ cursor: "pointer" }} onClick={() => setProfileOpen(true)}>
            <Typography sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: 15, lineHeight: 1.2 }}>{shopName}</Typography>
            <Typography sx={{ fontSize: 11, color: "#999" }}>Wholesale Ordering Portal</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={goToNewOrder}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: 12, bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" }, boxShadow: "0 2px 8px rgba(0,150,136,0.2)" }}>
            New Order
          </Button>
          <Tooltip title="Logout"><IconButton onClick={handleLogout} sx={{ color: "#bbb", "&:hover": { color: "#d32f2f" } }} size="small"><LogoutIcon sx={{ fontSize: 20 }} /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* ── CONTENT ── */}
      <Box sx={{ flex: 1, overflow: "auto", px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}>
        <Box sx={{ width: "100%" }}>

          {/* ── Credit Warning ── */}
          {creditStatus?.is_blocked && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5, fontSize: 13, fontWeight: 500, border: "1px solid #FFCDD2" }}>
              {creditStatus.has_overdue
                ? "Ordering is blocked due to overdue invoices. Please contact your supplier to resolve."
                : "Your credit limit has been exceeded. Please clear outstanding balance or contact your supplier."}
            </Alert>
          )}
          {creditStatus && !creditStatus.is_blocked && creditStatus.color === "orange" && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2.5, fontSize: 13, fontWeight: 500, border: "1px solid #FFE0B2" }}>
              Your credit usage is at {creditStatus.usage_percent}%. Please clear outstanding invoices soon.
            </Alert>
          )}

          {/* ── Summary Cards ── */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {[
              { icon: <LocalShippingIcon sx={{ fontSize: 16, color: "#2E7D32" }} />, iconBg: "#E8F5E9", label: "Next Delivery",
                value: nextDelivery ? dayjs(nextDelivery).format("ddd, MMM D") : "Not scheduled",
                sub: nextDelivery ? dayjs(nextDelivery).format("dddd") : "", color: nextDelivery ? "#1a1a1a" : "#ccc" },
              { icon: <PendingActionsIcon sx={{ fontSize: 16, color: "#F57F17" }} />, iconBg: "#FFF8E1", label: "Submitted",
                value: `${pendingOrders.length} order${pendingOrders.length !== 1 ? "s" : ""}`,
                sub: "Awaiting review", color: "#1a1a1a" },
              { icon: <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />, iconBg: "#E8F5E9", label: "Confirmed",
                value: `${confirmedOrders.length} order${confirmedOrders.length !== 1 ? "s" : ""}`,
                sub: "Ready for delivery", color: "#1a1a1a" },
              { icon: <HistoryIcon sx={{ fontSize: 16, color: "#7B1FA2" }} />, iconBg: "#F3E5F5", label: "Last Order",
                value: lastOrder ? `${lastOrder.items.length} items` : "No orders",
                sub: lastOrder ? dayjs(lastOrder.created_at).format("MMM D, YYYY") : "", color: lastOrder ? "#1a1a1a" : "#ccc" },
            ].map(c => (
              <Grid item xs={6} md={3} key={c.label}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: "1px solid #e8e8e8", bgcolor: "#fff", height: "100%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: c.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>{c.icon}</Box>
                    <Typography fontSize={11} color="#999" fontWeight={600} textTransform="uppercase" letterSpacing={0.3}>{c.label}</Typography>
                  </Box>
                  <Typography fontSize={16} fontWeight={800} color={c.color}>{c.value}</Typography>
                  {c.sub && <Typography fontSize={11} color="#999">{c.sub}</Typography>}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* ── Delivery Banner (Enhanced) ── */}
          {deliveryInfo && (
            <Paper elevation={0} sx={{ mb: 2.5, borderRadius: 2.5, overflow: "hidden", border: isCutoffPassed ? "1px solid #FFCDD2" : "1px solid #C8E6C9", bgcolor: isCutoffPassed ? "#FFF3E0" : "#E8F5E9" }}>
              <Box sx={{ p: { xs: 2, md: 2.5 }, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: isCutoffPassed ? "#E65100" : "#2E7D32", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <LocalShippingIcon sx={{ color: "#fff", fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography fontSize={15} fontWeight={800} color={isCutoffPassed ? "#BF360C" : "#1B5E20"}>
                    {isDeliveryDay ? "Delivery Today" : `Next Delivery: ${dayjs(deliveryInfo.date).format("dddd, MMMM D")}`}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 0.5 }}>
                    {deliveryInfo.delivery_time && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 13, color: isCutoffPassed ? "#BF360C" : "#388E3C" }} />
                        <Typography fontSize={12} color={isCutoffPassed ? "#BF360C" : "#388E3C"} fontWeight={500}>{deliveryInfo.delivery_time}</Typography>
                      </Box>
                    )}
                    {deliveryInfo.route_name && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AltRouteIcon sx={{ fontSize: 13, color: isCutoffPassed ? "#BF360C" : "#388E3C" }} />
                        <Typography fontSize={12} color={isCutoffPassed ? "#BF360C" : "#388E3C"} fontWeight={500}>{deliveryInfo.route_name}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 13, color: isCutoffPassed ? "#BF360C" : "#F57F17" }} />
                      <Typography fontSize={12} color={isCutoffPassed ? "#BF360C" : "#F57F17"} fontWeight={600}>
                        Cutoff: {deliveryInfo.cutoff_label}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {/* Countdown + CTA */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                  {countdown && !isCutoffPassed && (
                    <Chip icon={<AccessTimeIcon sx={{ fontSize: 14 }} />} label={countdown} size="small"
                      sx={{ bgcolor: "rgba(0,0,0,0.06)", color: "#E65100", fontWeight: 700, fontSize: 12, height: 28, "& .MuiChip-icon": { color: "#E65100" } }} />
                  )}
                  {isCutoffPassed && (
                    <Chip label="Cutoff Passed" size="small" sx={{ bgcolor: "#FFCDD2", color: "#C62828", fontWeight: 700, fontSize: 11, height: 26 }} />
                  )}
                  {!isCutoffPassed && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={goToNewOrder}
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: 13, px: 2.5, py: 1, bgcolor: "#2E7D32", "&:hover": { bgcolor: "#1B5E20" }, boxShadow: "0 2px 8px rgba(46,125,50,0.3)", whiteSpace: "nowrap" }}>
                      Place Order for {deliveryInfo.day_name}
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          {/* ── Quick Actions (when no delivery info) ── */}
          {!deliveryInfo && (
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: "1px solid #e8e8e8", bgcolor: "#fff", display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={goToNewOrder}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: 13, px: 3, py: 1, bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" }, boxShadow: "0 2px 8px rgba(0,150,136,0.25)" }}>
                New Order
              </Button>
              {lastOrder && (
                <Button variant="outlined" startIcon={<ReplayIcon />} onClick={() => goToReorder(lastOrder)}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13, px: 2.5, py: 1, borderColor: "#ddd", color: "#555", "&:hover": { borderColor: "#009688", color: "#009688", bgcolor: "#E0F2F1" } }}>
                  Reorder Last
                </Button>
              )}
            </Paper>
          )}

          {/* ── AI Suggested Order ── */}
          {(aiSuggestions.length > 0 || suggestionsLoading) && (
            <Paper elevation={0} sx={{ mb: 2.5, borderRadius: 2.5, border: "1px solid #e8e8e8", overflow: "hidden" }}>
              <Box sx={{ px: 2.5, py: 1.25, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #f0f0f0", bgcolor: "linear-gradient(135deg, #FAFAFA 0%, #F0FFF4 100%)" }}>
                <AutoAwesomeIcon sx={{ fontSize: 16, color: "#009688" }} />
                <Typography fontWeight={700} fontSize={13} color="#333">Suggested Order</Typography>
                <Chip label="AI Powered" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#E0F2F1", color: "#00796B", ml: 0.5 }} />
                {ordersAnalyzed > 0 && (
                  <Tooltip title={`Analyzed ${ordersAnalyzed} recent orders${suggestionsGenerated ? ` · Generated ${new Date(suggestionsGenerated).toLocaleString()}` : ""}`}>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "#bbb", cursor: "help" }} />
                  </Tooltip>
                )}
                <Box sx={{ ml: "auto" }} />
                {aiSuggestions.length > 0 && (
                  <Button size="small" variant="contained" onClick={() => goToSuggestedOrder()} startIcon={<ShoppingCartIcon sx={{ fontSize: 13 }} />}
                    sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" }, px: 2, py: 0.5 }}>
                    Add All to Cart
                  </Button>
                )}
              </Box>

              {suggestionsLoading ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <CircularProgress size={24} sx={{ color: "#009688", mb: 1 }} />
                  <Typography fontSize={12} color="text.secondary">Analyzing your order history...</Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={1.5}>
                    {aiSuggestions.map(sg => {
                      const TrendIcon = sg.trend === "increasing" ? TrendingUpIcon : sg.trend === "decreasing" ? TrendingDownIcon : TrendingFlatIcon;
                      const trendColor = sg.trend === "increasing" ? "#2e7d32" : sg.trend === "decreasing" ? "#d32f2f" : "#757575";
                      const confColor = sg.confidence >= 70 ? "#2e7d32" : sg.confidence >= 40 ? "#ed6c02" : "#9e9e9e";
                      return (
                        <Grid item xs={12} sm={6} md={4} key={sg.product_id}>
                          <Box sx={{
                            p: 1.75, borderRadius: 2, border: "1px solid #f0f0f0", bgcolor: "#fff",
                            "&:hover": { borderColor: "#B2DFDB", bgcolor: "#FAFFFE", boxShadow: "0 2px 8px rgba(0,150,136,0.08)" },
                            transition: "all 0.2s", position: "relative",
                          }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography fontSize={13} fontWeight={600} color="#333" noWrap>{sg.name}</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                                  <Typography fontSize={18} fontWeight={800} color="#009688">{sg.qty}</Typography>
                                  <Typography fontSize={11} color="#999" fontWeight={500}>{sg.unit}</Typography>
                                  <TrendIcon sx={{ fontSize: 14, color: trendColor, ml: 0.5 }} />
                                  {sg.trend !== "stable" && (
                                    <Typography fontSize={10} color={trendColor} fontWeight={600}>
                                      {sg.trend === "increasing" ? "+" : ""}{sg.trend_pct}%
                                    </Typography>
                                  )}
                                </Box>
                                {/* Confidence bar */}
                                <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                                  <Box sx={{ flex: 1, height: 3, bgcolor: "#f0f0f0", borderRadius: 2, overflow: "hidden" }}>
                                    <Box sx={{ height: "100%", width: `${sg.confidence}%`, bgcolor: confColor, borderRadius: 2, transition: "width 0.5s" }} />
                                  </Box>
                                  <Typography fontSize={9} color="#bbb" fontWeight={600}>{sg.confidence}%</Typography>
                                </Box>
                                {/* Reason */}
                                <Typography fontSize={10} color="#aaa" sx={{ mt: 0.5, lineHeight: 1.4 }} noWrap title={sg.reason}>
                                  {sg.reason}
                                </Typography>
                              </Box>
                              <IconButton size="small" onClick={() => goToSingleSuggested({ id: sg.product_id, name: sg.name, avgQty: sg.qty, unit: sg.unit, count: sg.frequency })}
                                sx={{ bgcolor: "#E0F2F1", color: "#009688", width: 32, height: 32, "&:hover": { bgcolor: "#B2DFDB" }, mt: 0.5 }}>
                                <AddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
            </Paper>
          )}

          {/* ── Empty State ── */}
          {orders.length === 0 && (
            <Paper elevation={0} sx={{ textAlign: "center", py: 8, borderRadius: 2.5, border: "1px solid #e8e8e8" }}>
              <ShoppingCartIcon sx={{ fontSize: 48, color: "#e0e0e0", mb: 1.5 }} />
              <Typography fontSize={16} fontWeight={600} color="text.secondary" mb={0.5}>No orders yet</Typography>
              <Typography fontSize={13} color="text.disabled" mb={3}>Start by creating your first order</Typography>
              <Button variant="contained" onClick={goToNewOrder} startIcon={<AddIcon />}
                sx={{ borderRadius: 2, bgcolor: "#009688", textTransform: "none", fontWeight: 600 }}>Create Order</Button>
            </Paper>
          )}

          {/* ── Orders Table (Enhanced) ── */}
          {orders.length > 0 && (
            <Paper elevation={0} sx={{ borderRadius: 2.5, border: "1px solid #e8e8e8", overflow: "hidden" }}>
              <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #f0f0f0", bgcolor: "#FAFAFA" }}>
                <Typography fontWeight={700} fontSize={14} color="#333">Order History</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {lastOrder && (
                    <Button size="small" variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 13 }} />} onClick={() => goToReorder(lastOrder)}
                      sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: "#ddd", color: "#555", "&:hover": { borderColor: "#009688", color: "#009688" } }}>
                      Reorder Last
                    </Button>
                  )}
                  <Typography fontSize={12} color="#bbb">{filteredOrders.length} of {orders.length}</Typography>
                </Box>
              </Box>

              {/* Status Tabs */}
              <Tabs
                value={orderTab}
                onChange={(_, v) => setOrderTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: "1px solid #f0f0f0",
                  bgcolor: "#fff",
                  px: 1,
                  minHeight: 40,
                  "& .MuiTab-root": {
                    minHeight: 40,
                    py: 1,
                    px: 2,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "none",
                    color: "#777",
                    "&.Mui-selected": { color: "#009688" },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#009688", height: 2 },
                }}
              >
                <Tab value="all"        label={`All (${tabCounts.all})`} />
                <Tab value="pending"    label={`Submitted (${tabCounts.pending})`} />
                <Tab value="processing" label={`Under Review (${tabCounts.processing})`} />
                <Tab value="confirmed"  label={`Confirmed (${tabCounts.confirmed})`} />
                <Tab value="delivered"  label={`Delivered (${tabCounts.delivered})`} />
              </Tabs>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#fff" }}>
                    {["Order", "Date", "Items", "Status", "Actions"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", py: 1.25, textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ borderBottom: "none", py: 5, textAlign: "center" }}>
                        <Typography fontSize={13} color="text.secondary">No orders in this category.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredOrders.map((order) => {
                    const sm = STATUS_META[order.status] ?? { label: order.status, bg: "#f5f5f5", color: "#666" };
                    return (
                      <TableRow key={order.id} sx={{ "&:hover": { bgcolor: "#FAFAFA" }, transition: "background 0.15s" }}>
                        <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                          <Typography fontSize={13} fontWeight={700} color="#333">#{order.id}</Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                          <Typography fontSize={12} fontWeight={600} color="#555">{dayjs(order.created_at).format("MMM D, YYYY")}</Typography>
                          <Typography fontSize={10} color="#bbb">{dayjs(order.created_at).format("h:mm A")}</Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                          <Typography fontSize={12} fontWeight={600} color="#555">{order.items.length} item{order.items.length !== 1 ? "s" : ""}</Typography>
                          <Typography fontSize={10} color="#bbb" noWrap sx={{ maxWidth: 220 }}>
                            {order.items.slice(0, 3).map(it => `${it.name} x${it.qty}`).join(", ")}{order.items.length > 3 && ` +${order.items.length - 3}`}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                          <Chip label={sm.label} size="small"
                            sx={{ fontSize: 10, fontWeight: 700, bgcolor: sm.bg, color: sm.color, height: 22, borderRadius: "11px" }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="View details">
                              <IconButton size="small" onClick={() => setViewOrder(order)} sx={{ color: "#bbb", "&:hover": { color: "#333" } }}>
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reorder">
                              <IconButton size="small" onClick={() => goToReorder(order)} sx={{ color: "#bbb", "&:hover": { color: "#009688" } }}>
                                <ContentCopyIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            {isAccepted(order.status) && (
                              <Tooltip title="Add note"><IconButton size="small" onClick={() => { setNoteOrderId(order.id); setNoteText(order.portal_note || ""); }} sx={{ color: "#bbb", "&:hover": { color: "#009688" } }}><NoteAddIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                            )}
                            {isEditable(order.status) && !isDeliveryDay && (
                              <Tooltip title="Edit order"><IconButton size="small" onClick={() => goToEditOrder(order)} sx={{ color: "#bbb", "&:hover": { color: "#009688" } }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                            )}
                            {isEditable(order.status) && isDeliveryDay && (
                              <Tooltip title="Request change"><IconButton size="small" onClick={() => { setRequestOrderId(order.id); setRequestMsg(""); }} sx={{ color: "#bbb", "&:hover": { color: "#ef6c00" } }}><SendIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      </Box>

      {/* ── VIEW ORDER DIALOG ── */}
      <Dialog open={!!viewOrder} onClose={() => setViewOrder(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {viewOrder && (
          <Box sx={{ p: 0 }}>
            <Box sx={{ bgcolor: "#009688", px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography fontWeight={700} fontSize={17} color="#fff">Order #{viewOrder.id}</Typography>
                <Typography fontSize={12} color="rgba(255,255,255,0.7)">{dayjs(viewOrder.created_at).format("MMMM D, YYYY [at] h:mm A")}</Typography>
              </Box>
              <Chip label={STATUS_META[viewOrder.status]?.label || viewOrder.status} size="small"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700, fontSize: 11 }} />
            </Box>
            <Box sx={{ p: 3 }}>
              {viewOrder.portal_note && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: 12 }}><strong>Your note:</strong> {viewOrder.portal_note}</Alert>
              )}
              {viewOrder.notes && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontSize: 12 }}><strong>Supplier note:</strong> {viewOrder.notes}</Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#999" }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#999", width: 70 }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#999", width: 70 }}>Unit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewOrder.items.map((it, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>{it.name}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{it.qty}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#999" }}>{it.unit || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontSize={14} fontWeight={700}>{viewOrder.items.length} item{viewOrder.items.length !== 1 ? "s" : ""}</Typography>
                <Chip label={STATUS_META[viewOrder.status]?.label || viewOrder.status} size="small"
                  sx={{ fontSize: 11, fontWeight: 700, bgcolor: STATUS_META[viewOrder.status]?.bg, color: STATUS_META[viewOrder.status]?.color }} />
              </Box>
              <Box sx={{ display: "flex", gap: 1, mt: 2.5 }}>
                <Button variant="outlined" startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />} onClick={() => { setViewOrder(null); goToReorder(viewOrder); }}
                  sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#ddd", color: "#555", "&:hover": { borderColor: "#009688", color: "#009688" } }}>
                  Reorder
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="text" onClick={() => setViewOrder(null)} sx={{ borderRadius: 2, textTransform: "none", color: "#999" }}>Close</Button>
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* ── PROFILE DIALOG ── */}
      <Dialog open={profileOpen} onClose={() => setProfileOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: "#009688", px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ width: 50, height: 50, borderRadius: 2, border: "2px solid rgba(255,255,255,0.3)", overflow: "hidden", bgcolor: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {shop?.customerImg ? <img src={`${LARAVEL_URL}/${shop.customerImg}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>{shopName.charAt(0)}</Typography>}
          </Box>
          <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>{shopName}</Typography><Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>{shop?.portal_email}</Typography></Box>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Button size="small" startIcon={<LockResetIcon sx={{ fontSize: 14 }} />} onClick={() => { setProfileOpen(false); setChangePwOpen(true); }} sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 600, textTransform: "none", borderRadius: 1.5 }} variant="outlined">Password</Button>
            <Button size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => { setProfileOpen(false); setEditingProfile(true); setProfileForm({ ...shop }); }} sx={{ color: "#009688", bgcolor: "#fff", fontSize: 11, fontWeight: 600, textTransform: "none", borderRadius: 1.5, "&:hover": { bgcolor: "#E0F2F1" } }} variant="contained">Edit</Button>
          </Box>
        </Box>
        <Box sx={{ p: 3 }}>
          {pwSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setPwSuccess("")}>{pwSuccess}</Alert>}
          {nextDelivery && (<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5, p: 2, bgcolor: "#E8F5E9", borderRadius: 2 }}><LocalShippingIcon sx={{ color: "#2E7D32" }} /><Box><Typography fontSize={11} color="#2E7D32" fontWeight={600}>Next Expected Delivery</Typography><Typography fontSize={14} color="#1B5E20" fontWeight={700}>{dayjs(nextDelivery).format("dddd, MMMM D, YYYY")}</Typography></Box></Box>)}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            {[{ icon: <PhoneIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />, label: "Phone", value: shop?.customerMobileNum }, { icon: <EmailIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />, label: "Email", value: shop?.customerEmail }, { icon: <PhoneIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />, label: "Company Phone", value: shop?.customerCompanyPhoneNumebr }, { icon: <EmailIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />, label: "Company Email", value: shop?.customerCompanyEmail }].filter(f => f.value).map(f => (
              <Box key={f.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>{f.icon}<Box><Typography fontSize={10} color="text.disabled" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>{f.label}</Typography><Typography fontSize={13}>{f.value}</Typography></Box></Box>
            ))}
          </Box>
          {address && (<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2 }}><LocationOnIcon sx={{ fontSize: 16, color: "#9e9e9e", mt: 0.25 }} /><Box><Typography fontSize={10} color="text.disabled" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>Address</Typography><Typography fontSize={13}>{address}</Typography></Box></Box>)}
        </Box>
      </Dialog>

      <Dialog open={editingProfile} onClose={() => setEditingProfile(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Typography fontWeight={700} fontSize={17} mb={2.5}>Edit Profile</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            {[{ key: "customerFirstName", label: "First Name" }, { key: "customerLastName", label: "Last Name" }, { key: "customerMobileNum", label: "Mobile" }, { key: "customerEmail", label: "Email" }, { key: "customerCompanyPhoneNumebr", label: "Company Phone" }, { key: "customerCompanyEmail", label: "Company Email" }, { key: "customerStreetNumber", label: "Street Number" }, { key: "customerStreetName", label: "Street Name" }, { key: "customerCity", label: "City" }, { key: "customerProvince", label: "Province" }, { key: "customerPostalCode", label: "Postal Code" }].map(({ key, label }) => (
              <TextField key={key} size="small" label={label} value={(profileForm as any)[key] || ""} onChange={(e) => setProfileForm(f => ({ ...f, [key]: e.target.value }))} sx={inputSx} />
            ))}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
            <Button variant="outlined" onClick={() => setEditingProfile(false)} sx={{ borderRadius: 2, textTransform: "none", borderColor: "#ccc", color: "#666" }}>Cancel</Button>
            <Button variant="contained" disabled={savingProfile} onClick={saveProfile} sx={{ borderRadius: 2, textTransform: "none", bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" } }}>{savingProfile ? "Saving..." : "Save Changes"}</Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={changePwOpen} onClose={() => setChangePwOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Typography fontWeight={700} fontSize={17} mb={2}>Change Password</Typography>
          {pwError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>{pwError}</Alert>}
          <TextField size="small" fullWidth label="Current Password" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} sx={{ mb: 2, ...inputSx }} />
          <TextField size="small" fullWidth label="New Password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} sx={{ mb: 2, ...inputSx }} />
          <TextField size="small" fullWidth label="Confirm New Password" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} sx={{ mb: 3, ...inputSx }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={() => { setChangePwOpen(false); setPwError(""); }} sx={{ borderRadius: 2, textTransform: "none", borderColor: "#ccc", color: "#666" }}>Cancel</Button>
            <Button variant="contained" disabled={pwLoading} onClick={handleChangePw} sx={{ borderRadius: 2, textTransform: "none", bgcolor: "#009688" }}>{pwLoading ? "Saving..." : "Update Password"}</Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={!!noteOrderId} onClose={() => setNoteOrderId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Typography fontWeight={700} fontSize={16} mb={2}>Add Note to Order #{noteOrderId}</Typography>
          <TextField size="small" fullWidth multiline rows={3} label="Note" value={noteText} onChange={(e) => setNoteText(e.target.value)} sx={{ mb: 2, ...inputSx }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={() => setNoteOrderId(null)} sx={{ borderRadius: 2, textTransform: "none", borderColor: "#ccc", color: "#666" }}>Cancel</Button>
            <Button variant="contained" disabled={savingNote} onClick={handleAddNote} sx={{ borderRadius: 2, textTransform: "none", bgcolor: "#009688" }}>{savingNote ? "Saving..." : "Save Note"}</Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={!!requestOrderId} onClose={() => setRequestOrderId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Typography fontWeight={700} fontSize={16} mb={1}>Request Change</Typography>
          <Typography fontSize={13} color="text.secondary" mb={2}>Today is the delivery day. Send a change request to your supplier.</Typography>
          <TextField size="small" fullWidth multiline rows={3} label="Describe what you'd like to change" value={requestMsg} onChange={(e) => setRequestMsg(e.target.value)} sx={{ mb: 2, ...inputSx }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={() => setRequestOrderId(null)} sx={{ borderRadius: 2, textTransform: "none", borderColor: "#ccc", color: "#666" }}>Cancel</Button>
            <Button variant="contained" disabled={savingRequest || !requestMsg.trim()} onClick={handleSendRequest} sx={{ borderRadius: 2, textTransform: "none", bgcolor: "#ef6c00", "&:hover": { bgcolor: "#e65100" } }}>{savingRequest ? "Sending..." : "Send Request"}</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default PortalDashboard;
